import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import React, { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { KPI_CATEGORIES, STATUS_TYPES } from "@shared/constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Create theme context
type Theme = "light" | "dark" | "system";
type ThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider(props: ThemeProviderProps) {
  const { 
    children, 
    defaultTheme = "system", 
    storageKey = "theme" 
  } = props;
  
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return React.createElement(
    ThemeProviderContext.Provider,
    { value },
    children
  );
};

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};

// Format currency
export function formatCurrency(amount: number | string): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(numAmount);
}

// Format percentage
export function formatPercent(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  return `${numValue.toFixed(1)}%`;
}

// Get status color based on status
export function getStatusColor(status: string): string {
  switch (status) {
    case STATUS_TYPES.GREEN:
      return 'bg-success text-white';
    case STATUS_TYPES.YELLOW:
      return 'bg-warning text-white';
    case STATUS_TYPES.RED:
      return 'bg-destructive text-white';
    default:
      return 'bg-primary text-white';
  }
}

// Get icon for KPI category
export function getKpiIcon(category: string): string {
  switch (category) {
    case KPI_CATEGORIES.REVENUE:
      return 'currency-usd';
    case KPI_CATEGORIES.PATIENTS:
      return 'account-group';
    case KPI_CATEGORIES.NEW_CLIENTS:
      return 'account-plus';
    case KPI_CATEGORIES.RETENTION:
      return 'account-check';
    case KPI_CATEGORIES.SATISFACTION:
      return 'star';
    case KPI_CATEGORIES.OPERATIONS:
      return 'cog';
    case KPI_CATEGORIES.MARKETING:
      return 'bullhorn';
    case KPI_CATEGORIES.FINANCE:
      return 'bank';
    default:
      return 'chart-line';
  }
}

// Format date in a readable format
export function formatDate(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

// Format time in a readable format
export function formatTime(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
}

// Get initials from full name
export function getInitials(fullName: string): string {
  if (!fullName || typeof fullName !== 'string') {
    return '';
  }
  
  return fullName
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase();
}

// Calculate percentage from value and goal
export function calculatePercentage(value: string | number, goal: string | number): number {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  const numGoal = typeof goal === 'string' ? parseFloat(goal.replace(/[^0-9.-]+/g, '')) : goal;
  
  if (numGoal === 0) return 0;
  return Math.round((numValue / numGoal) * 100);
}

// Determine status based on percentage
export function getStatusFromPercentage(percentage: number): 'green' | 'yellow' | 'red' {
  if (percentage >= 90) return 'green';
  if (percentage >= 75) return 'yellow';
  return 'red';
}

// Parse markdown content
export function parseMarkdown(markdown: string): string {
  if (!markdown) return '';
  
  // Basic markdown parsing (for simplicity)
  const html = markdown
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/^\* (.*$)/gim, '<li>$1</li>')
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*)\*/gim, '<em>$1</em>')
    .replace(/\n/gim, '<br>');
    
  return html;
}
